import { parseDate } from '@datepicker-react/hooks';
import moment, { Moment, unitOfTime } from 'moment';
import 'moment/locale/tr';

export type DateType = Moment;
export type SearchBarDateType = Date | null;

export const DEFAULT_DATE_FORMAT = 'DD.MM.YYYY';
export const URL_DATE_FORMAT = 'YYYY-MM-DD';
export const HOTEL_SEARCH_DATE_FORMAT = URL_DATE_FORMAT;
export const FLIGHT_SEARCH_DATE_FORMAT = URL_DATE_FORMAT;
export const CAR_RENTAL_SEARCH_DATE_FORMAT = URL_DATE_FORMAT;
export const DEFAULT_DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm';
export const CAR_RENTAL_DATE_TIME_FORMAT = 'DD.MM.YYYY - HH:mm';
export const DATE_WITH_MONTH_TEXT_FORMAT = 'DD MMMM YYYY';
export const GTM_DATE_FORMAT = 'YYYYMMDD';
export const FORM_DATE_FORMAT = 'DD/MM/YYYY';

export const formatMomentDate = (
  momentDate: Date | Moment | null | undefined,
  toDateFormat: string = DEFAULT_DATE_FORMAT
): string =>
  momentDate
    ? `${moment(momentDate, DEFAULT_DATE_FORMAT).format(toDateFormat)}`
    : '';

export const formatISOToDate = (
  date: string | undefined,
  format = DEFAULT_DATE_FORMAT
): string => moment.utc(date).format(format);

export const convertDateToMomentObject = (
  date: string | undefined = '',
  fromDateFormat: string = DEFAULT_DATE_FORMAT
) => moment(date || undefined, fromDateFormat);

export const convertStringDateFormat = (
  date: string | undefined = '',
  fromDateFormat: string,
  toDateFormat: string
) =>
  formatMomentDate(
    convertDateToMomentObject(date || undefined, fromDateFormat),
    toDateFormat
  );

export const getEndOfDay = () => moment().endOf('day');

export function isBeforeDay(a: DateType, b: DateType) {
  if (!moment.isMoment(a) || !moment.isMoment(b)) return false;

  const aYear = a.year();
  const aMonth = a.month();

  const bYear = b.year();
  const bMonth = b.month();

  const isSameYear = aYear === bYear;
  const isSameMonth = aMonth === bMonth;

  if (isSameYear && isSameMonth) return a.date() < b.date();
  if (isSameYear) return aMonth < bMonth;
  return aYear < bYear;
}

export function isBeforeDateTime(a: DateType, b: DateType) {
  if (!moment.isMoment(a) || !moment.isMoment(b)) return false;

  return a.isBefore(b);
}

export function isAfterDateTime(a: DateType, b: DateType) {
  if (!moment.isMoment(a) || !moment.isMoment(b)) return false;

  return a.isAfter(b);
}

export const getCurrentDate = () => {
  return moment();
};

export const getTomorrow = () => {
  return getNDaysLater(getCurrentDate(), 1);
};

export function isInclusivelyAfterDay(a: DateType, b: DateType) {
  if (!moment.isMoment(a) || !moment.isMoment(b)) return false;
  return !isBeforeDay(a, b);
}

export const getNMonthsLater = (fromDate: DateType, amount: number) => {
  return moment(fromDate).add(amount, 'month');
};

export const getNDaysLater = (fromDate: DateType, amount: number) => {
  return moment(fromDate).add(amount, 'days');
};

export const getNDaysLaterFromToday = (amount: number) => {
  return moment().add(amount, 'days');
};

export const getAge = (birthDate: string) =>
  moment().diff(formatISOToDate(birthDate), 'years');

export const getAgeFromFormattedString = (
  formattedBirthDate: string,
  format: string
) => {
  return moment().diff(moment(formattedBirthDate, format), 'years');
};

export const getTimeOfISODate = (date?: string): string =>
  date ? moment.utc(date).format('HH:mm') : '';

/**
 * @description Returns only the day of the date in ISO date format
 * @example 2020-01-01T00:00:00.000Z => 01.01.2020 | 01/01/2020
 * @param date string
 * @param withDotSeperator boolean
 * @returns string
 */
export const getDateOfISODate = (
  date?: string,
  withDotSeperator = false
): string =>
  date
    ? moment
        .utc(date)
        .format(withDotSeperator ? DEFAULT_DATE_FORMAT : FORM_DATE_FORMAT)
    : '';

export const getDateTimeOfISODate = (date?: string): string =>
  date ? moment.utc(date).format(CAR_RENTAL_DATE_TIME_FORMAT) : '';

export const getWeekDayName = (
  date?: string,
  dateFormat = DEFAULT_DATE_FORMAT
) => moment(date, dateFormat).format('dddd');

export const getTimeDifferenceFromNow = (
  date?: string,
  unitOfTime: unitOfTime.Diff = 'days'
) => moment().diff(moment(date, DEFAULT_DATE_FORMAT), unitOfTime);

export const convertDateAsOnlyMonthString = (date: string) =>
  moment(date).format('Do MMMM YYYY');

export const daysBetween = (first: Date, second: Date) => {
  // Copy date parts of the timestamps, discarding the time parts.
  const one = new Date(first.getFullYear(), first.getMonth(), first.getDate());
  const two = new Date(
    second.getFullYear(),
    second.getMonth(),
    second.getDate()
  );

  // Do the math.
  const millisecondsPerDay = 1000 * 60 * 60 * 24;
  const millisBetween = two.getTime() - one.getTime();
  const days = millisBetween / millisecondsPerDay;

  // Round down.
  return Math.floor(days);
};

export const validRoomChildAge = (date: string) => {
  let isValid = true;

  const ageFromYears = getAgeFromFormattedString(date, FORM_DATE_FORMAT);

  const notValidAge = isAfterDateTime(
    moment(parseDate(date, String(`dd/MM/yyyy`), new Date())),
    moment(
      parseDate(
        getCurrentDate().format('DD/MM/YYYY'),
        String(`dd/MM/yyyy`),
        new Date()
      )
    )
  );

  !date || String(date) === '' || ageFromYears >= 15 || notValidAge
    ? (isValid = false)
    : (isValid = true);
  return isValid;
};

export const formatDateForDateInput = (date?: moment.Moment | null) => {
  if (!date) return null;

  return [date.format('D'), date.format('M'), date.format('YYYY')].join('.');
};
